<template>
  <div class="lavadero-metricas">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="info"
      class="mb-0"
    />

    <!-- Filtros -->
    <v-row class="lavadero-metricas__filters px-2 pt-2" dense>
      <v-col cols="12" sm="4" class="py-1">
        <FechaPicker
          v-model="filtros.fecha_desde"
          label="Desde"
          :filled="false"
          dense
        />
      </v-col>
      <v-col cols="12" sm="4" class="py-1">
        <FechaPicker
          v-model="filtros.fecha_hasta"
          label="Hasta"
          :filled="false"
          dense
        />
      </v-col>
      <v-col cols="12" sm="4" class="py-1 d-flex align-center">
        <v-btn
          color="primary"
          small
          :loading="loading"
          @click="fetchData"
        >
          <v-icon left small>fas fa-search</v-icon>
          Buscar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Summary Cards -->
    <v-row v-if="resumen" class="lavadero-metricas__summary px-2 pt-2" dense>
      <v-col cols="6" sm="4" md="2" v-for="card in summaryCards" :key="card.label">
        <v-card outlined :color="card.color" dark class="text-center">
          <v-card-text class="pa-3">
            <div class="text-caption">{{ card.label }}</div>
            <div class="text-h5 font-weight-bold">{{ card.value }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Daily Table -->
    <v-row v-if="metricas.length > 0" class="lavadero-metricas__table px-2 pt-2">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="metricas"
          :items-per-page="-1"
          :hide-default-footer="true"
          dense
          class="elevation-1"
        />
      </v-col>
    </v-row>

    <!-- Back to kanban -->
    <v-row class="px-2 pt-4">
      <v-col cols="12">
        <v-btn
          color="primary"
          small
          outlined
          :to="'/taller/lavadero'"
        >
          <v-icon left small>fas fa-arrow-left</v-icon>
          Volver al tablero
        </v-btn>
      </v-col>
    </v-row>

    <!-- Empty state -->
    <v-row v-if="!loading && metricas.length === 0 && buscado" class="px-2 pt-4">
      <v-col cols="12" class="text-center pa-8">
        <v-icon size="48" color="grey">fas fa-chart-bar</v-icon>
        <p class="text-body-1 grey--text mt-2">No se encontraron métricas para el período seleccionado.</p>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import FechaPicker from "@/components/util/FechaPicker.vue";
import moment from "moment";

export default {
  name: "LavaderoMetricasHistoricas",
  components: {
    FechaPicker,
  },
  data() {
    return {
      filtros: {
        fecha_desde: moment().subtract(7, "days").format("DD/MM/YYYY"),
        fecha_hasta: moment().format("DD/MM/YYYY"),
      },
      headers: [
        { text: "Fecha", value: "fecha", sortable: true },
        { text: "Ingresados", value: "ingresados_dia", sortable: true },
        { text: "Asignados", value: "asignados_dia", sortable: true },
        { text: "Finalizados", value: "finalizados_dia", sortable: true },
        { text: "Vueltos a pendiente", value: "vueltos_a_pendiente_dia", sortable: true },
      ],
      metricas: [],
      loading: false,
      buscado: false,
    };
  },
  computed: {
    resumen() {
      if (this.metricas.length === 0) return null;
      return {
        asignados_total: this.metricas.reduce((s, r) => s + r.asignados_dia, 0),
        finalizados_total: this.metricas.reduce((s, r) => s + r.finalizados_dia, 0),
        vueltos_total: this.metricas.reduce((s, r) => s + r.vueltos_a_pendiente_dia, 0),
        ingresados_total: this.metricas.reduce((s, r) => s + r.ingresados_dia, 0),
      };
    },
    summaryCards() {
      if (!this.resumen) return [];
      return [
        { label: "Ingresados", value: this.resumen.ingresados_total, color: "blue darken-1" },
        { label: "Asignados", value: this.resumen.asignados_total, color: "amber darken-2" },
        { label: "Finalizados", value: this.resumen.finalizados_total, color: "green darken-1" },
        { label: "Vueltos a pendiente", value: this.resumen.vueltos_total, color: "orange darken-1" },
        { label: "Pend. inicio", value: this.metricas.reduce((s, r) => s + r.pendientes_inicio, 0), color: "deep-purple darken-1" },
        { label: "Pend. cierre", value: this.metricas.reduce((s, r) => s + r.pendientes_cierre, 0), color: "deep-purple darken-1" },
      ];
    },
  },
  methods: {
    normalizarFechaParaApi(fecha) {
      if (!fecha) return "";

      const fechaNormalizada = moment(fecha, ["DD/MM/YYYY", "YYYY-MM-DD"], true);
      return fechaNormalizada.isValid() ? fechaNormalizada.format("YYYY-MM-DD") : "";
    },
    async fetchData() {
      this.loading = true;
      this.buscado = true;
      try {
        const result = await this.$store.dispatch("lavadero/fetchMetricasHistoricas", {
          fecha_desde: this.normalizarFechaParaApi(this.filtros.fecha_desde),
          fecha_hasta: this.normalizarFechaParaApi(this.filtros.fecha_hasta),
          sucursal: 1,
        });
        if (result && result.exito === 1) {
          this.metricas = result.data || [];
        } else {
          this.metricas = [];
        }
      } catch (err) {
        console.error("[LavaderoMetricas] Error:", err.message);
        this.metricas = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.lavadero-metricas {
  padding-bottom: 24px;
}
</style>
